import { useEffect, useState } from "react";
import {
	DialogContent,
	Typography,
	Box,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Stack,
	Chip,
	TextField,
	Tooltip,
	useTheme,
	IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faHashtag,
	faCalendarAlt,
	faUser,
	faPerson,
	faTruck,
	faWarehouse,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../hooks/useAuth";
import { useDepotAccess } from "../../hooks/useDepotAccess";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { Util } from "../../util/Util";
import { AddCircleOutline, RemoveCircleOutline, ReplayOutlined } from "@mui/icons-material";
import ConfirmDialog from "../../components/ConfirmDialog";
import QuantityStepperInput from "../../components/QuantityStepperInput";
import { qtyMin, roundQty, addMoney, lineTotal } from "../../util/quantity";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
} from "../../components/MoleDialogShell";
import RefreshButton from "../../components/RefreshButton";
import { DepotEnum, MYSQL_LIMITS, OperationEnum } from "../../util/constants";
import { useAppSocket } from "../../providers/AppSocketContext";
import { parseISODay } from "../../util/businessClock";

interface ReturnItem {
	item: number;
	item_name: string;
	item_code: string;
	quantity: number;
	maxQuantity: number;
	amount: number;
	total: number;
	expiry: string | null;
	isFractional?: boolean;
}

interface ReturnedRecord {
	item: number;
	item_name: string;
	item_code: string;
	quantity: number;
	amount: number;
}

interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
	onSuccess?: () => void;
}

const lineKey = (itemId: number, amount: number) => `${itemId}:${amount}`;

const ReturnSupplyModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [record, setRecord] = useState<any>(null);
	const [returnsRecord, setReturnsRecord] = useState<any>(null);
	const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
	const [returnedItems, setReturnedItems] = useState<ReturnedRecord[]>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [returnDate, setReturnDate] = useState<Date | null>(null);
	const [description, setDescription] = useState("");
	const [errorInput, setErrorInput] = useState("");

	const { api } = useAuth();
	const { ensureRoute } = useDepotAccess();
	const { appSettings } = useAppSocket();
	const theme = useTheme();
	const warningColor = theme.palette.warning.main;
	const { setLoadingMessage, queueReRun, t } = useUI();

	const canValidate = returnItems.length > 0 && returnDate !== null;

	const fetchData = async () => {
		if (String(bundle?.batch || "").startsWith("ret:")) {
			toast.warn(t("common:toast.warn.noRecord"));
			toggle();
			return;
		}
		const res = await api.get(`supplies/${bundle.batch}`);
		if (res) {
			if (res.status === 200) {
				setRecord(res.data);
				setReturnDate(new Date());
				setDescription("");
				return;
			}
			if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else {
				console.error("Failed to fetch supply:", res);
			}
		}
		toggle();
	};

	const fetchReturns = async () => {
		const res = await api.get(`supplies/returns/${bundle.batch}`);
		if (res) {
			if (res.status === 200) {
				setReturnsRecord(res.data);
				return;
			}
			if (res.status === 401 || res.status === 422) {
				queueReRun(fetchReturns);
			} else if (res.status === 404) {
				setReturnsRecord({ records: [] });
			} else {
				console.error("Failed to fetch supply returns:", res);
			}
		}
	};

	useEffect(() => {
		if (isOpen && bundle?.batch) {
			fetchData();
			setReturnItems([]);
			setReturnedItems([]);
			setReturnsRecord(null);
		}
	}, [isOpen, bundle?.batch]);

	useEffect(() => {
		if (record) {
			fetchReturns();
		}
	}, [record]);

	useEffect(() => {
		if (!returnsRecord?.records) return;

		const mergedReturned: ReturnedRecord[] = [];
		returnsRecord.records.forEach((rec: any) => {
			const amount = rec.amount;
			const existing = mergedReturned.find(
				(row) => row.item === rec.item_id && row.amount === amount,
			);
			if (existing) {
				existing.quantity += rec.quantity;
			} else {
				mergedReturned.push({
					item: rec.item_id,
					item_name: rec.item_name,
					item_code: rec.item_code,
					quantity: rec.quantity,
					amount,
				});
			}
		});
		setReturnedItems(mergedReturned);
	}, [returnsRecord]);

	const isItemInReturnList = (item: any) =>
		returnItems.some(
			(row) => row.item === item.item_id && row.amount === item.amount,
		);

	const getReturnedQty = (item: any) =>
		returnedItems.find(
			(row) => row.item === item.item_id && row.amount === item.amount,
		)?.quantity ?? 0;

	const handleAddItem = (item: any) => {
		if (isItemInReturnList(item)) return;
		const returnedQty = getReturnedQty(item);
		const remaining = roundQty(item.quantity - returnedQty);
		if (remaining <= 0) return;

		setReturnItems((prev) => [
			...prev,
			{
				item: item.item_id,
				item_name: item.item_name,
				item_code: item.item_code,
				quantity: remaining,
				maxQuantity: remaining,
				amount: item.amount,
				total: item.total,
				expiry: item.expiry || null,
				isFractional: item.is_fractional === true || item.IsFractional === true,
			},
		]);
	};

	const handleRemoveItem = (item: ReturnItem) => {
		setReturnItems((prev) =>
			prev.filter(
				(row) => !(row.item === item.item && row.amount === item.amount),
			),
		);
	};

	const handleQuantityChange = (item: ReturnItem, newQty: number) => {
		const min = qtyMin(!!item.isFractional);
		const clamped = roundQty(Math.max(min, Math.min(newQty, item.maxQuantity)));
		setReturnItems((prev) =>
			prev.map((row) =>
				row.item === item.item && row.amount === item.amount
					? { ...row, quantity: clamped, total: lineTotal(clamped, row.amount) }
					: row,
			),
		);
	};

	const handleConfirm = async () => {
		if (!record?.details) return;
		if (!ensureRoute(OperationEnum.ENTRY, DepotEnum.EXTERNAL_IN, record.details.depot_id)) return;
		setLoadingMessage("SAVE");

		const details = {
			batch: record.details.batch,
			supplier: record.details.supplier_id,
			receiver: record.details.client_id,
			depot: record.details.depot_id,
			sup_date: Util.formatISODate(returnDate || new Date()),
			ref: record.details.reference,
			description: description || `Return for ${record.details.reference}`,
		};

		const payload = {
			details,
			records: returnItems.map(({ item, item_name, quantity, amount, expiry }) => ({
				item,
				item_desc: item_name,
				quantity,
				unit_cost: amount,
				expiry: expiry ? String(expiry).slice(0, 10) : null,
				batch: record.details.batch,
			})),
		};

		const res = await api.post("/supplies/returns", payload);
		if (res?.status === 201) {
			toast.success(t("common:toast.success.return"), { autoClose: 3000 });
			onSuccess?.();
			toggle();
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleConfirm);
		}

		setLoadingMessage(null);
	};

	const returnTotal = returnItems.reduce(
		(sum, row) => addMoney(sum, lineTotal(row.quantity, row.amount)),
		0,
	);

	const handleDialogClose = (_event: object, reason: string) => {
		if (reason === "backdropClick" || reason === "escapeKeyDown") return;
		toggle();
	};

	const originalDate = record?.details?.date
		? (parseISODay(record.details.date) as Date)
		: new Date();

	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={handleDialogClose}
				maxWidth="lg"
				fullWidth
				themeColor={warningColor}
				paperOptions={{ maxHeight: "95vh" }}
			>
				<MoleDialogHeader
					eyebrow={t("common:return.supplyTitle")}
					title={record?.details?.reference || bundle.reference}
					icon={<ReplayOutlined />}
					onClose={toggle}
					themeColor={warningColor}
				/>

				<DialogContent sx={{ p: 4 }}>
					{record ? (
						<Box>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, md: 6 }}>
									<Paper
										variant="outlined"
										sx={{
											p: 2,
											mb: 3,
											bgcolor: "background.default",
											borderStyle: "dashed",
										}}
									>
										<Stack spacing={1}>
											<DetailItem
												icon={faHashtag}
												label={t("common:supplySituation.reference")}
												value={record?.details?.reference}
											/>
											<DetailItem
												icon={faTruck}
												label={t("common:tables.supplier")}
												value={record?.details?.supplier}
											/>
											<DetailItem
												icon={faPerson}
												label={t("common:tables.observer")}
												value={record?.details?.client}
											/>
											<DetailItem
												icon={faWarehouse}
												label={t("common:tables.depot")}
												value={record?.details?.depot}
											/>
											<DetailItem
												icon={faCalendarAlt}
												label={t("common:payments.modal.supplyDate")}
												value={Util.formatDate(record?.details?.date, "full")}
											/>
											<DetailItem
												icon={faUser}
												label={t("common:report.supplyBy")}
												value={record?.details?.user}
											/>
										</Stack>
									</Paper>
								</Grid>

								<Grid size={{ xs: 12, md: 6 }}>
									<Paper
										variant="outlined"
										sx={{
											p: 3,
											mb: 3,
											bgcolor: "background.paper",
											border: "1px solid",
											borderColor: "primary.light",
										}}
									>
										<Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
											{t("common:return.details")}
										</Typography>
										<Grid container spacing={0}>
											<Grid size={{ xs: 12 }}>
												<LocalizationProvider dateAdapter={AdapterDateFns}>
													<DatePicker
														label={
															<span>
																{t("common:return.date")}:&nbsp;&nbsp;
																<RefreshButton
																	isWorking={false}
																	onClick={() => setReturnDate(new Date())}
																/>
															</span>
														}
														value={returnDate}
														minDate={originalDate}
														maxDate={new Date()}
														onChange={(newDate) => {
															if (
																!newDate
																|| Util.compareStrings(newDate.toString(), "Invalid Date")
																|| (newDate instanceof Date ? newDate : newDate.toDate()) < originalDate
																|| (newDate instanceof Date ? newDate : newDate.toDate()) > new Date()
															) {
																setErrorInput("dateInput");
																setTimeout(() => setErrorInput(""), 1500);
																return;
															}
															const date = newDate instanceof Date
																? newDate
																: newDate.toDate();
															setReturnDate(date);
														}}
														slotProps={{
															textField: {
																id: "supplyReturnDateInput",
																size: "small",
																fullWidth: true,
																variant: "filled",
																error: errorInput === "dateInput",
																helperText:
																	errorInput === "dateInput"
																		? t("common:validation.invalidDate")
																		: "",
															},
														}}
													/>
												</LocalizationProvider>
											</Grid>
											<Grid size={{ xs: 12 }}>
												<TextField
													id="supplyReturnDescriptionInput"
													size="small"
													label={t("common:ui.labels.notes")}
													variant="filled"
													fullWidth
													type="text"
													value={description}
													inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
													helperText={`${description.length}/${MYSQL_LIMITS.TEXT.maxLength}`}
													onChange={(e) => setDescription(e.target.value)}
													placeholder={t("common:return.placeholder")}
												/>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
							</Grid>

							<Grid container spacing={3}>
								<Grid size={{ xs: 12, md: 6 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
										{t("common:return.supplyItems")}
									</Typography>
									<TableContainer
										component={Paper}
										elevation={0}
										sx={{
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 2,
											maxHeight: 400,
										}}
									>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 700 }}>
														{t("common:report.description")}
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>
														{t("common:report.quantity")}
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>
														{t("common:report.unitPrice")}
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>
														{t("common:report.total")}
													</TableCell>
													<TableCell align="center" sx={{ fontWeight: 700 }} />
												</TableRow>
											</TableHead>
											<TableBody>
												{record.records?.map((item: any) => {
													const added = isItemInReturnList(item);
													const returnedQty = getReturnedQty(item);
													const allReturned = roundQty(item.quantity - returnedQty) <= 0;
													return (
														<TableRow
															key={lineKey(item.item_id, item.amount)}
															sx={{ opacity: allReturned || added ? 0.4 : 1 }}
														>
															<TableCell>
																<Typography variant="body2" fontWeight={600}>
																	{item.item_name}
																</Typography>
																<Typography variant="caption" color="text.secondary">
																	{item.item_code || t("common:ui.placeholders.notAvailable")}
																</Typography>
																<Typography variant="body2" color="text.secondary">
																	{t("common:return.alreadyReturned")}: {returnedQty}
																</Typography>
															</TableCell>
															<TableCell align="right">
																{Util.formatNumber(item.quantity)}
															</TableCell>
															<TableCell align="right">
																{Util.formatNumber(item.amount)}
															</TableCell>
															<TableCell align="right" sx={{ fontWeight: 700 }}>
																{Util.formatNumber(item.total)}
															</TableCell>
															<TableCell align="center">
																<Tooltip title={t("common:return.add")}>
																	<span>
																		<IconButton
																			size="small"
																			color="warning"
																			disabled={allReturned || added}
																			onClick={() => handleAddItem(item)}
																		>
																			<AddCircleOutline fontSize="small" />
																		</IconButton>
																	</span>
																</Tooltip>
															</TableCell>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Grid>

								<Grid size={{ xs: 12, md: 6 }}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										sx={{ mb: 1 }}
									>
										<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
											{t("common:return.returnItems")}
										</Typography>
										{returnItems.length > 0 && (
											<Chip
												label={`${t("common:report.total")}: ${Util.formatNumber(returnTotal)}`}
												color="warning"
												size="small"
												sx={{ fontWeight: 700 }}
											/>
										)}
									</Stack>
									<TableContainer
										component={Paper}
										elevation={0}
										sx={{
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 2,
											maxHeight: 400,
										}}
									>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 700 }}>
														{t("common:report.description")}
													</TableCell>
													<TableCell align="center" sx={{ fontWeight: 700 }}>
														{t("common:return.returnQty")}
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>
														{t("common:report.unitPrice")}
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>
														{t("common:report.total")}
													</TableCell>
													<TableCell align="center" sx={{ fontWeight: 700 }} />
												</TableRow>
											</TableHead>
											<TableBody>
												{returnItems.length === 0 ? (
													<TableRow>
														<TableCell colSpan={5} align="center">
															<Typography
																variant="body2"
																color="text.secondary"
																fontStyle="italic"
																sx={{ py: 4 }}
															>
																{t("common:return.noItems")}
															</Typography>
														</TableCell>
													</TableRow>
												) : (
													returnItems.map((item) => (
														<TableRow key={lineKey(item.item, item.amount)}>
															<TableCell>
																<Typography variant="body2" fontWeight={600}>
																	{item.item_name}
																</Typography>
																<Typography variant="caption" color="text.secondary">
																	{item.item_code || t("common:ui.placeholders.notAvailable")}
																</Typography>
															</TableCell>
															<TableCell align="center">
																<Box sx={{ width: 88, mx: "auto" }}>
																	<QuantityStepperInput
																		compact
																		size="small"
																		fullWidth
																		value={item.quantity}
																		fractional={item.isFractional}
																		min={qtyMin(!!item.isFractional)}
																		max={item.maxQuantity}
																		onChange={(newValue) =>
																			handleQuantityChange(item, newValue)
																		}
																	/>
																</Box>
															</TableCell>
															<TableCell align="right">
																{Util.formatNumber(item.amount)}
															</TableCell>
															<TableCell align="right" sx={{ fontWeight: 700 }}>
																{Util.formatNumber(lineTotal(item.quantity, item.amount))}
															</TableCell>
															<TableCell align="center">
																<Tooltip title={t("common:return.remove")}>
																	<IconButton
																		size="small"
																		color="error"
																		onClick={() => handleRemoveItem(item)}
																	>
																		<RemoveCircleOutline fontSize="small" />
																	</IconButton>
																</Tooltip>
															</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</Grid>
							</Grid>
						</Box>
					) : (
						<Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
							<CircularProgress thickness={5} size={48} sx={{ color: "warning.main" }} />
							<Typography sx={{ mt: 2, color: "text.secondary" }}>
								{t("common:report.fetchingDetails")}
							</Typography>
						</Stack>
					)}
				</DialogContent>

				<MoleDialogActions>
					<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
					<MolePrimaryButton
						color="warning"
						startIcon={<ReplayOutlined />}
						onClick={() => setConfirmOpen(true)}
						disabled={!canValidate}
					>
						{t("common:return.confirm")}
					</MolePrimaryButton>
				</MoleDialogActions>
			</MoleDialog>

			<ConfirmDialog
				onConfirm={handleConfirm}
				isOpen={confirmOpen}
				setIsOpen={setConfirmOpen}
				title={t("common:return.confirmTitle")}
				message={
					<>
						{t("common:return.confirmSupplyMessage", {
							count: returnItems.length,
							total: Util.formatCurrency(
								returnTotal,
								appSettings?.BIZ_CURRENCY?.value || "USD",
							),
							reference: bundle?.reference,
						})}
					</>
				}
				confirmBtnColor="warning"
				confirmBtnVariant="contained"
				confirmBtnText={<>{t("common:return.confirm")}</>}
			/>
		</>
	);
};

const DetailItem = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="center">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: "#64748b" }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
			{label}:
		</Typography>
		<Typography variant="body2" fontWeight={600}>
			{value || "—"}
		</Typography>
	</Stack>
);

export default ReturnSupplyModal;